import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import { alpha } from '@mui/material/styles';
import { ProtectedContent } from '../../components/ProtectedContent';
import COLORS from '../../constants/colors';
import { isProduction as isProductionApi } from '../../constants/environment';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { useAppDispatch } from '../../redux/hooks';
import { fetchUserBankInfo } from '../../redux/modules/user/user.actions';
import { madkhoulBankInfoSelector, otherBankInfoSelector, userSelector } from '../../redux/modules/user/user.selectors';
import * as permissions from '../../utils/permissions';
import { AddFundsDialog } from './AddFundsDialog';
import { UserTestDepositDialog } from './UserTestDepositDialog';
import { CARD_BORDER, Field, FieldGrid, SectionCard } from '../../components/Section';

interface BankInfoTabProps {
  id: number;
}

export function BankInfoTab({ id }: BankInfoTabProps) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [testDepositOpen, setTestDepositOpen] = useState(false);
  const { error, fetchBankInfoStatus } = useSelector(userSelector);
  const bankInfo = useSelector(madkhoulBankInfoSelector);
  const otherBankInfoItems = useSelector(otherBankInfoSelector);
  const isLoading = fetchBankInfoStatus === fetchStatuses.pending;

  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

  useEffect(() => {
    if (id) {
      dispatch(fetchUserBankInfo(id));
    }
  }, []);

  const onAddFunds = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const canShowAddFunds = !isProduction && Boolean(bankInfo?.userBankDetails?.accountNumber);

  // Both production checks, not just the env name: this one writes a fake deposit through the real path.
  const canShowTestDeposit = canShowAddFunds && !isProductionApi;

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: '16px', borderRadius: '8px' }}>
          {error.message}
        </Alert>
      )}

      <SectionCard
        title="Madkhol account"
        action={
          canShowAddFunds ? (
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {canShowTestDeposit && (
                <ProtectedContent items={[permissions.PaymentPermission.paymentStatement]}>
                  <LoadingButton
                    id="AddFundsWithLedgerButton"
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() => setTestDepositOpen(true)}
                    endIcon={<AddIcon />}
                    loadingPosition="end"
                    sx={{ textTransform: 'none' }}
                  >
                    Add funds (with ledger)
                  </LoadingButton>
                </ProtectedContent>
              )}
              <ProtectedContent items={[permissions.UserBank.updateUserBankAmount]}>
                <LoadingButton
                  id="AddMoneyButton"
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={onAddFunds}
                  endIcon={<AddIcon />}
                  loadingPosition="end"
                  sx={{ textTransform: 'none' }}
                >
                  Add funds
                </LoadingButton>
              </ProtectedContent>
            </Box>
          ) : undefined
        }
      >
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: '8px' }} />
        ) : (
          <FieldGrid>
            <Field label="Madkhol IBAN number" value={bankInfo?.iban} />
            <Field label="Account number">
              <span id="AccountNumberValue">{bankInfo?.userBankDetails?.accountNumber || '—'}</span>
            </Field>
          </FieldGrid>
        )}
      </SectionCard>

      <SectionCard
        title="Other bank accounts"
        description={otherBankInfoItems.length ? `${otherBankInfoItems.length} account(s)` : undefined}
        sx={{ mt: '16px' }}
      >
        {otherBankInfoItems.length === 0 ? (
          <Typography sx={{ fontSize: '0.875rem', color: COLORS.LIGHT_GRAY }}>
            No other bank accounts on file.
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: '12px' }}>
            {otherBankInfoItems.map((bo) => (
              <Box
                key={`bank-info-item-${bo.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '14px',
                  p: '14px 16px',
                  borderRadius: '10px',
                  border: `1px solid ${CARD_BORDER}`,
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(COLORS.PRIMARY_BLUE, 0.1),
                    color: '#1E72A8',
                  }}
                >
                  <AccountBalanceIcon sx={{ fontSize: '18px' }} />
                </Avatar>

                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', mb: '8px' }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: COLORS.MAIN_DARK }}>
                      {bo.userBankDetails.beneficiaryName || '—'}
                    </Typography>
                    {bo?.isPrimaryAccount && (
                      <Chip
                        label="Primary"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600, borderRadius: '6px' }}
                      />
                    )}
                  </Box>
                  <FieldGrid>
                    <Field label="IBAN" value={bo.userBankDetails.iban} />
                    <Field label="SWIFT" value={bo.userBankDetails.swiftCode} />
                  </FieldGrid>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </SectionCard>

      <AddFundsDialog open={open} onClose={handleClose} bankInfo={bankInfo} />
      <UserTestDepositDialog open={testDepositOpen} userId={id} onClose={() => setTestDepositOpen(false)} />
    </>
  );
}
